import React, { useCallback } from 'react';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import { AsyncSelect } from '@atlaskit/select';
import type { ObjectTypeAttributeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { useSelectAttributeOptionsSearch } from '../services/index.tsx';
import type { SelectAttributeOption } from './types.tsx';

export type Props = {
	isMultiSelect: boolean;
	objectTypeAttributeId: ObjectTypeAttributeId;
	defaultSelectedValues: string[];
	validationState: 'default' | 'error';
	autoFocus: boolean;
	menuIsOpen?: boolean;
	value: string[];
	isClearable?: boolean;
	shouldInputRenderValue?: boolean;
	onChange: (arg1: SelectAttributeOption[]) => void;
	isDisabled?: boolean;
};

const isSelectAttributeOption = (selectOption: unknown): selectOption is SelectAttributeOption =>
	typeof selectOption === 'object' &&
	selectOption != null &&
	'value' in selectOption &&
	'label' in selectOption;

const isSelectAttributeOptionArray = (
	selectOptions: unknown,
): selectOptions is SelectAttributeOption[] =>
	selectOptions != null &&
	Array.isArray(selectOptions) &&
	selectOptions.length > 0 &&
	isSelectAttributeOption(selectOptions[0]);

export const SelectAttributeOptionPicker = (props: Props) => {
	const {
		objectTypeAttributeId,
		isMultiSelect,
		onChange,
		defaultSelectedValues,
		validationState,
		autoFocus,
		menuIsOpen,
		value,
	} = props;

	const { fetch } = useSelectAttributeOptionsSearch();
	const loadOptions = useCallback(
		(query: string, cb: (arg1: SelectAttributeOption[]) => void) => {
			fetch({ objectTypeAttributeId, query }).then((response) => {
				cb(
					response?.map((optionValue: string) => ({
						value: optionValue,
						label: optionValue,
					})) ?? [],
				);
			});
		},
		[fetch, objectTypeAttributeId],
	);
	const [debouncedLoadOptions] = useDebouncedCallback(
		(query: string, cb: (arg1: SelectAttributeOption[] | undefined) => void) =>
			loadOptions(query, cb),
		400,
	);

	return (
		<AsyncSelect
			{...selectInAModalStyleFixProps}
			isClearable={props.isClearable}
			controlShouldRenderValue={props.shouldInputRenderValue}
			defaultOptions
			autoFocus={autoFocus}
			menuIsOpen={menuIsOpen}
			menuPlacement="auto"
			value={
				value.length > 0
					? value.map<SelectAttributeOption>((optionValue) => ({
							label: optionValue,
							value: optionValue,
						}))
					: defaultSelectedValues.map<SelectAttributeOption>((optionValue) => ({
							label: optionValue,
							value: optionValue,
						}))
			}
			isMulti={isMultiSelect}
			loadOptions={debouncedLoadOptions}
			onChange={(values) => {
				if (!values || (isMultiSelect && Array.isArray(values) && values.length === 0)) {
					onChange([]);
				} else if (isMultiSelect && isSelectAttributeOptionArray(values)) {
					onChange(values);
				} else if (isSelectAttributeOption(values) || typeof values === 'string') {
					onChange([values]);
				}
			}}
			validationState={validationState}
			classNamePrefix="insight-object-attribute-select"
			isDisabled={props.isDisabled}
		/>
	);
};

SelectAttributeOptionPicker.defaultProps = {
	defaultSelectedValues: [],
	value: [],
	validationState: 'default',
	autoFocus: false,
	isClearable: true,
	shouldInputRenderValue: true,
};
